import { UserMenu } from "@/components/CodeGen";
import { Button } from "@/components/ui/button";
import {
  FpSidebar,
  FpSidebarContent,
  FpSidebarHeader,
  FpSidebarInset,
  FpSidebarProvider,
  FpSidebarRail,
  FpSidebarTrigger,
  useFpSidebar,
} from "@/components/ui/fp-sidebar";
import { useHandler, useInputFocus } from "@/hooks";
import { useLocation, useRouter } from "@tanstack/react-router";
import { type PropsWithChildren, memo } from "react";
import { RecentItems } from "../CodeGen";
import { Logo } from "./logo";

export function CodeGenLayout({ children }: PropsWithChildren) {
  return (
    <div className="h-screen flex flex-col">
      <FpSidebarProvider defaultOpen={false} forceSheet={true}>
        <FpSidebar
          forceSheet={true}
          collapsible="offcanvas"
          variant="floating"
          className="bg-popover [&>[data-sidebar=sidebar]]:!bg-popover"
          style={
            {
              "--sidebar-background": "hsl(var(--popover))",
              "--sidebar-foreground": "hsl(var(--popover-foreground))",
            } as React.CSSProperties
          }
        >
          <FpSidebarHeader className="h-12 p-0 border-b-0">
            <div className="flex items-center gap-2 h-12 px-4 min-h-12">
              <Logo />
              <FpSidebarTrigger />
            </div>
          </FpSidebarHeader>
          <FpSidebarContent>
            <div className="flex-1 flex flex-col min-h-0">
              <RecentItems />
              <NewChatButton />
              <UserMenu />
            </div>
          </FpSidebarContent>
          <FpSidebarRail />
        </FpSidebar>
        <FpSidebarInset className="flex-1 flex flex-col min-h-0">
          {children}
        </FpSidebarInset>
      </FpSidebarProvider>
    </div>
  );
}

const NewChatButton = memo(() => {
  const focusInput = useInputFocus();
  const router = useRouter();
  const pathname = useLocation({ select: (l) => l.pathname });
  const { setOpen } = useFpSidebar();

  const handleNewChat = useHandler(() => {
    // Close sidebar when creating new chat
    setOpen(false);

    // Only focus input if we're already on the codegen page
    if (pathname === "/codegen") {
      focusInput();
      return;
    }
    router.navigate({ to: "/codegen" });
  });

  return (
    <Button
      className="mx-4 text-sm"
      size="sm"
      variant="primary"
      onClick={handleNewChat}
    >
      New Project
    </Button>
  );
});
